import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useRecoilState } from "recoil";
import {
  TextField,
  Tabs,
  Tab,
  Box,
  Grid,
  Button,
  Typography,
  Paper,
  Pagination,
  Skeleton
} from "@mui/material";
import { searchContestState, filterContestTab } from "../store/search";
import HotContestBanner from "./HotContestBanner";
// @ts-ignore
import fireIcon from "../assets/images/fireIcon.png";
// @ts-ignore
import MemegramArmyImage from "../assets/images/memegram.png";
import { useRecoilValue } from "recoil";
import { useQuery } from "react-query";
import { ContestStatusType, getAllContests } from "../apis/contestApi";

type Content = "active" | "paused" | "completed" | "all";
const PAGE_LIMIT = 100;

const Contests = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useRecoilState(filterContestTab);
  const [searchContestTerm, setSearchContestTerm] =
    useRecoilState(searchContestState);
  // const searchContests = useRecoilValue(searchContestState);
  const [page, setPage] = useState(1);

  const { data: hotContestsData } = useQuery(
    "getHotContests",
    () =>
      getAllContests({
        statuses: ["pending"],
        pagination: {
          limit: 3,
          offset: 0,
          orderBy: "votes_count",
          orderDirection: "desc"
        }
      }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      refetchOnReconnect: false
    }
  );

  const { data: contestsData, isFetching: isAllContestsFetching } = useQuery(
    `getAllContests-${tabValue?.value}-${page}`,
    () =>
      getAllContests({
        statuses:
          tabValue?.value === "all"
            ? ["completed", "pending"]
            : [
                tabValue?.value === "active"
                  ? "pending"
                  : (tabValue?.value as ContestStatusType)
              ],
        pagination: {
          limit: PAGE_LIMIT,
          offset: (page - 1) * PAGE_LIMIT,
          orderBy: "start_time",
          orderDirection: "desc"
        }
      }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      refetchOnReconnect: false
    }
  );

  const contests = contestsData?.contests ?? [];
  const totalContestsCount = contestsData?.totalCount ?? 0;
  const hotContests = hotContestsData?.contests ?? [];

  const filteredContests = useMemo(() => {
    if (searchContestTerm.value) {
      return contests.filter((contest) =>
        (contest.name || contest.contest_name)
          ?.toLowerCase()
          ?.includes(searchContestTerm.value.toLowerCase())
      );
    }
    return contests;
  }, [contests, searchContestTerm]);
  const filteredContestsCount = filteredContests.length;

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleTabChange = (value: Content) => {
    setTabValue({ value });
  };

  const handleSearchContest = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSearchContestTerm({ ...searchContestTerm, [name]: value });
  };

  const handleDetailNavigation = (contest: any) => {
    navigate(`/contests/${contest.id}`);
  };

  const renderPagination = () => {
    if (totalContestsCount <= PAGE_LIMIT) {
      return null;
    }
    return (
      <Grid item xs={12}>
        <Pagination
          count={Math.ceil(totalContestsCount / PAGE_LIMIT)}
          page={page}
          disabled={false}
          onChange={handleChangePage}
          sx={{
            "& .MuiPaginationItem-root": {
              color: "#fff"
            },
            "& .Mui-selected": {
              bgcolor: "#ffe263 !important",
              color: "#245c9c"
            }
          }}
        />
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        position: "relative",
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        padding: "0px 16px"
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          <Box display={"flex"} alignItems={"center"}>
            <Typography variant="h6" sx={{ color: "#ffe263" }}>
              Hot Contests
            </Typography>
            <div
              style={{
                height: 24,
                width: 24,
                backgroundImage: `url("${fireIcon}")`,
                backgroundSize: "cover"
              }}
            />
          </Box>
        </Grid>

        {hotContests.map((contest) => (
          <HotContestBanner
            detailPage={() => handleDetailNavigation(contest)}
            key={contest.id}
            bannerImage={contest.contest_banner}
            contestDescription={contest.description}
            logoImage={contest.logo_url}
            title={
              contest.name
                ? `${`${contest.name} ${
                    contest.symbol ? ` | ${contest.symbol}` : ""
                  }`}`
                : contest.contest_name
            }
            subtitle={contest.name ? contest.contest_name : contest.description}
            activitiesCount={contest.activities_count}
            usersCount={contest.users_count}
            votesCount={contest.votes_count}
            contestStatus={
              contest.status === "pending" && !contest.is_expired
                ? "Active"
                : contest.status
            }
            expiryTime={contest.end_time}
          />
        ))}

        {/* Create contest static layout */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Paper
            sx={{
              height: "325px",
              position: "relative",
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
              flexDirection: "column",
              borderRadius: "32px",
              overflow: "hidden",
              backgroundColor: "#245c9c",
              transition: "transform 0.3s",
              "&:hover": {
                transform: "scale(1.05)"
              }
            }}
          >
            <img
              alt="banner"
              src={MemegramArmyImage}
              style={{
                position: "absolute",
                height: "100%",
                width: "100%",
                objectFit: "cover",
                zIndex: 1
              }}
            />
            <Button
              variant="contained"
              sx={{
                top: "30%",
                width: "85%",
                backgroundColor: "#ffb703",
                border: "1px solid #245c9c",
                color: "#245c9c",
                borderRadius: "24px",
                height: "48px",
                "&:hover": {
                  backgroundColor: "#245c9c",
                  transform: "scale(1.05)",
                  transition: "transform 0.15s ease-in-out",
                  color: "#f5f5f5",
                  border: "1px solid #ffb703"
                },
                position: "relative",
                zIndex: 3
              }}
              onClick={() => {
                window.open("https://t.me/memegramAI", "_blank");
              }}
            >
              Join Our Army
            </Button>
            {/* </Box> */}
          </Paper>
        </Grid>

        {/* Join Our Army */}
        {hotContests?.length < 3 && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Paper
              sx={{
                height: "325px",
                position: "relative",
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                flexDirection: "column",
                borderRadius: "32px",
                overflow: "hidden",
                backgroundColor: "#245c9c",
                transition: "transform 0.3s",
                "&:hover": {
                  transform: "scale(1.05)"
                }
              }}
            >
              <img
                alt="banner"
                src="https://pbs.twimg.com/profile_images/1803730917321555968/dLBhIFan_400x400.jpg"
                style={{
                  position: "absolute",
                  height: "100%",
                  width: "100%",
                  objectFit: "cover",
                  zIndex: 1
                }}
              />
              <Button
                variant="contained"
                sx={{
                  top: "30%",
                  width: "85%",
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #245c9c",
                  color: "#245c9c",
                  borderRadius: "24px",
                  height: "48px",
                  "&:hover": {
                    backgroundColor: "#245c9c",
                    transform: "scale(1.05)",
                    transition: "transform 0.15s ease-in-out",
                    color: "#f5f5f5",
                    border: "1px solid #ffb703"
                  },
                  position: "relative",
                  zIndex: 3
                }}
                onClick={() => {
                  window.open("https://t.me/memegramAiBot", "_blank");
                }}
              >
                Create contest
              </Button>
            </Paper>
          </Grid>
        )}
        <Grid item xs={12}>
          <Tabs
            value={tabValue.value}
            onChange={(_e, value) => handleTabChange(value)}
            aria-label="Contents Category tabs"
            sx={{
              width: "100%",
              ".MuiTab-root": {
                color: "#ffffffcc",
                padding: {
                  xs: "0px",
                  sm: "16px"
                },
                fontSize: {
                  xs: "11px",
                  sm: "14px"
                }
              },
              ".Mui-selected": {
                color: "#ffe263 !important",
                padding: {
                  xs: "0px",
                  sm: "16px"
                },
                fontSize: {
                  xs: "11px",
                  sm: "14px"
                }
              }
            }}
          >
            <Tab label="Active" value={"active"} />
            <Tab label="Completed" value={"completed"} />
            <Tab label="All" value={"all"} />
          </Tabs>
        </Grid>
        <Grid item xs={12}>
          <TextField
            variant="outlined"
            placeholder="Search contests"
            name="value"
            size="small"
            onChange={handleSearchContest}
            InputLabelProps={{ style: { color: "white" } }}
            sx={{
              height: "auto",
              width: {
                xs: "100%",
                md: "50%",
                lg: "40%"
              },
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "2px solid #ffe263 !important",
                  borderRadius: "8px"
                },
                "&:hover fieldset": {
                  border: "2px solid #ffe263 !important",
                  borderRadius: "8px"
                }
              }
            }}
            inputProps={{ style: { color: "white" } }}
          />
        </Grid>

        {/* Top pagination */}
        {renderPagination()}

        {/* Load Skeleton when contests length is 0 */}
        {isAllContestsFetching && contests.length === 0 && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Paper
              sx={{
                height: "325px",
                position: "relative",
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                flexDirection: "column",
                borderRadius: "32px",
                overflow: "hidden",
                backgroundColor: "#245c9c",
                transition: "transform 0.3s",
                "&:hover": {
                  transform: "scale(1.05)"
                }
              }}
            >
              <Skeleton
                variant="rectangular"
                width={"100%"}
                height={"50vh"}
                sx={{
                  borderRadius: "16px",
                  backgroundColor: "#245c9c"
                }}
              />
            </Paper>
          </Grid>
        )}

        {/* Contests list */}
        {filteredContests.map((contest) => (
          <HotContestBanner
            detailPage={() => handleDetailNavigation(contest)}
            key={contest.id}
            bannerImage={contest.contest_banner}
            contestDescription={contest.description}
            logoImage={contest.logo_url}
            title={
              contest.name
                ? `${`${contest.name} ${
                    contest.symbol ? ` | ${contest.symbol}` : ""
                  }`}`
                : contest.contest_name
            }
            subtitle={contest.name ? contest.contest_name : contest.description}
            activitiesCount={contest.activities_count}
            usersCount={contest.users_count}
            votesCount={contest.votes_count}
            contestStatus={
              contest.status === "pending" && !contest.is_expired
                ? "Active"
                : contest.status
            }
            expiryTime={contest.end_time}
          />
        ))}

        {/* Bottom pagination */}
        {renderPagination()}
      </Grid>
    </Box>
  );
};

export default Contests;
